<template>
    <div class="default-home employees-page">
        <div class="employees-page__shell">
            <LoaderComponent v-if="loading" />
            <div v-else>
                <div class="employees-page__header">
                    <div>
                        <span class="employees-page__eyebrow">Accès</span>
                        <h5 class="title employees-page__title">{{ $tl("labels.routes.employees") }}</h5>
                        <p class="employees-page__subtitle">Gérez les utilisateurs, leurs rôles et leurs accès aux établissements.</p>
                    </div>
                    <router-link
                        :class="{ disabled: !has_right_to_create_employee }"
                        :to="{ name: 'employees.add', params: { restaurant_id: restaurant_id } }"
                        class="btn btn-sm btn-success btn-circle employees-page__action">
                        <feather type="plus"></feather>
                        <span>{{ $tl("labels.routes.addEmployee") }}</span>
                    </router-link>
                </div>

                <div v-if="error" class="alert alert-danger">
                    {{ error }}
                </div>
                <div v-else class="employees-page__card">
                    <div class="employees-page__table-wrap" v-if="employees && employees.length > 0">
                        <table class="capitalize table table-sm employees-page__table">
                            <thead>
                                <tr>
                                    <th>{{ $tl("labels.form.fullname") }}</th>
                                    <th>{{ $tl("labels.form.email") }}</th>
                                    <th>{{ $tl("labels.form.role") }}</th>
                                    <th class="text-end">{{ $tl("labels.form.actions.title") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="employee in employees" :key="employee.id">
                                    <td class="employees-page__name">{{ employee.firstname }} {{ employee.lastname }}</td>
                                    <td>{{ employee.email }}</td>
                                    <td style="text-transform: capitalize">
                                        <span class="employees-page__role">{{ getRoleLabel(employee.role) }}</span>
                                    </td>
                                    <td class="text-end employees-page__actions">
                                        <router-link
                                            :class="{ disabled: !has_right_to_update_employee }"
                                            :to="{
                                                name: 'employees.edit',
                                                params: { restaurant_id: restaurant_id, employee_id: employee.id },
                                            }"
                                            class="btn btn-sm btn-success btn-square"
                                            v-tooltip="getTooltip($t('labels.form.actions.edit'))">
                                            <feather type="edit" />
                                        </router-link>
                                        <button
                                            :disabled="!has_right_to_delete_employee"
                                            @click="deleteEmployee(employee.id)"
                                            :data-id="employee.id"
                                            class="btn btn-sm btn-danger btn-square"
                                            v-tooltip="getTooltip($t('labels.form.actions.delete'))">
                                            <feather type="trash-2" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="employees-page__empty">
                        <em>{{ $tl("labels.employees.empty") }}</em>
                    </div>
                    <div class="employees-page__pagination" v-if="links.prev || links.next">
                        <a class="btn btn-sm btn-success btn-circle" v-if="links.prev" href="javascript:" @click="fetchData(links.prev)">
                            {{ $tl("labels.form.actions.previous") }}
                        </a>
                        <a class="btn btn-sm btn-success btn-circle" v-if="links.next" href="javascript:" @click="fetchData(links.next)">
                            {{ $tl("labels.form.actions.next") }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import LoaderComponent from "../../../components/LoaderComponent";
import RolesEnum from "../../../mixins/enums/RolesEnum";

export default {
    data() {
        return {
            loading: false,
            restaurant_id: this.$route.params.restaurant_id,
            employees: null,
            paginate: false,
            links: {
                next: null,
                prev: null,
            },
            error: null,
        };
    },
    mixins: [RolesEnum],
    created() {
        this.fetchData();
    },
    computed: {
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_create_employee() {
            return this.rights.includes("default.employees.create");
        },
        has_right_to_update_employee() {
            return this.rights.includes("default.employees.update");
        },
        has_right_to_delete_employee() {
            return this.rights.includes("default.employees.delete");
        },
    },
    methods: {
        fetchData(api_url) {
            this.error = this.employees = this.links.next = this.links.prev = null;
            this.loading = true;
            if (!api_url) {
                api_url = "/api/employees";
            }
            axios
                .get(api_url)
                .then((response) => {
                    this.employees = response.data.data;
                    if (response.data.links) {
                        this.paginate = true;
                        if (response.data.links.next) {
                            this.links.next = response.data.links.next;
                        }
                        if (response.data.links.prev) {
                            this.links.prev = response.data.links.prev;
                        }
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.$notify({
                        group: "notification",
                        type: "error",
                        title: this.getErrorMsgFromErrorResponse(error),
                    });
                });
        },
        deleteEmployee(delete_employee_id) {
            if (!confirm(this.$tl("questions.employees.delete"))) {
                return;
            }
            this.loading = true;

            axios
                .delete(`/api/employees/${delete_employee_id}`)
                .then(() => {
                    this.loading = false;
                    this.$notify({
                        group: "notification",
                        type: "success",
                        title: this.$tl("success.employees.deleted"),
                    });
                    this.fetchData();
                })
                .catch((error) => {
                    this.loading = false;
                    this.$notify({
                        group: "notification",
                        type: "error",
                        title: this.getErrorMsgFromErrorResponse(error),
                    });
                });
        },
    },
    watch: {
        "$route.params.restaurant_id": function () {
            this.restaurant_id = this.$route.params.restaurant_id;
            this.fetchData();
        },
    },
    components: {
        LoaderComponent,
    },
};
</script>

<style scoped>
.employees-page {
    min-height: calc(100vh - 72px);
    padding: 28px 18px 40px;
    background: linear-gradient(180deg, #f7fbfc 0%, #eef8fa 100%);
}

.employees-page__shell {
    max-width: 1080px;
    margin: 0 auto;
}

.employees-page__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
}

.employees-page__eyebrow {
    display: inline-flex;
    margin-bottom: 8px;
    color: var(--main-color-light, #30a4b7);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
}

.employees-page__title {
    margin: 0;
    color: #142f3c;
    font-size: 1.45rem;
    font-weight: 700;
}

.employees-page__subtitle {
    margin: 8px 0 0;
    color: #6c7f88;
}

.employees-page__card {
    padding: 10px 18px 18px;
    border: 1px solid rgba(48, 164, 183, 0.12);
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 18px 45px rgba(15, 58, 72, 0.07);
}

.employees-page__table-wrap {
    overflow-x: auto;
}

.employees-page__table {
    margin-bottom: 0;
}

.employees-page__table thead th {
    border-top: 0;
    border-bottom: 1px solid rgba(48, 164, 183, 0.14);
    color: #6c7f88;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
}

.employees-page__table tbody td {
    vertical-align: middle;
    padding-top: 14px;
    padding-bottom: 14px;
    border-top: 1px solid #edf4f6;
}

.employees-page__name {
    color: #173746;
    font-weight: 700;
}

.employees-page__role {
    display: inline-flex;
    padding: 5px 10px;
    border-radius: 999px;
    color: #256a78;
    font-size: 0.82rem;
    font-weight: 700;
    background: rgba(48, 164, 183, 0.1);
}

.employees-page__actions .btn {
    margin-left: 6px;
}

.employees-page__empty {
    padding: 34px 16px;
    color: #6c7f88;
    text-align: center;
}

.employees-page__pagination {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}

button:disabled {
    cursor: default;
}

@media (max-width: 767.98px) {
    .employees-page {
        padding: 20px 12px 32px;
    }

    .employees-page__header {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
